// A companion window's own navigation (the app's page, Board #2737): the window keeps the page's pages in a router of
// its own, in memory, so a link inside a companion's page (a governance menu entry, a source, an element, a rule) opens
// in the window and its back goes back in the window, while the app stays underneath on the page's address. Only an
// address that is none of the companions' pages leaves the window (it closes; the page's router takes the address).
import * as React from 'react';
import {
  MemoryRouter,
  Route,
  Routes,
  UNSAFE_DataRouterContext,
  UNSAFE_DataRouterStateContext,
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router';

/** Where the window is: its address and how many steps back it can go. */
export interface WindowPlace {
  path: string;
  depth: number;
}

/** The window's controls for its header (rendered outside the window's router). */
export interface WindowControls {
  back(): void;
}

const NO_ROUTE = { outlet: null, matches: [], isDataRoute: false };

/** Reports the window's address and history depth up, and hands out its back. */
function Tracker({
  onPlace,
  controls,
}: {
  onPlace: (p: WindowPlace) => void;
  controls: React.RefObject<WindowControls | null>;
}) {
  const location = useLocation();
  const type = useNavigationType();
  const navigate = useNavigate();
  const depth = React.useRef(0);
  const first = React.useRef(true);
  React.useEffect(() => {
    if (first.current) first.current = false;
    else if (type === 'PUSH') depth.current += 1;
    else if (type === 'POP') depth.current = Math.max(0, depth.current - 1);
    onPlace({ path: `${location.pathname}${location.search}`, depth: depth.current });
  }, [location.key, location.pathname, location.search, type, onPlace]);
  React.useImperativeHandle(controls, () => ({ back: () => void navigate(-1) }), [navigate]);
  return null;
}

/** An address that is no page of the window: the page's own router goes there (the window closes with the app). */
function Leave({ to }: { to: (path: string) => void }) {
  const location = useLocation();
  React.useEffect(() => to(`${location.pathname}${location.search}${location.hash}`), [location, to]);
  return null;
}

export function WindowRouter({
  start,
  pages,
  page,
  onPlace,
  controls,
  onLeave,
}: {
  /** The window's first address (`/data-dictionary`). */
  start: string;
  /** The companions' pages by route path (`data-sources/:id`). */
  pages: Record<string, () => Promise<React.ComponentType>>;
  /** A page's component (cached, lazy). */
  page: (key: string) => React.ComponentType | null;
  onPlace: (p: WindowPlace) => void;
  controls: React.RefObject<WindowControls | null>;
  /** An address that is none of the window's pages: the window closes and the page goes there. */
  onLeave: (path: string) => void;
}) {
  return (
    // The window's router is not nested in the page's: the page's router contexts are cleared below it.
    <UNSAFE_DataRouterContext.Provider value={null}>
      <UNSAFE_DataRouterStateContext.Provider value={null}>
        <UNSAFE_LocationContext.Provider value={null as never}>
          <UNSAFE_RouteContext.Provider value={NO_ROUTE}>
            <MemoryRouter initialEntries={[start]}>
              <Tracker onPlace={onPlace} controls={controls} />
              <Routes>
                {Object.keys(pages).map((key) => {
                  const Page = page(key);
                  return Page ? <Route key={key} path={`/${key}`} element={<Page />} /> : null;
                })}
                <Route path="*" element={<Leave to={onLeave} />} />
              </Routes>
            </MemoryRouter>
          </UNSAFE_RouteContext.Provider>
        </UNSAFE_LocationContext.Provider>
      </UNSAFE_DataRouterStateContext.Provider>
    </UNSAFE_DataRouterContext.Provider>
  );
}
